// src/features/game/components/ReEntryListEditor.tsx

import React from 'react';

import {
  Plus,
  X,
} from 'lucide-react';

import type {
  ReEntry,
} from '../types/game.types';

import {
  formatCurrency,
  formatNumber,
} from '../../../shared/utils/formatUtils';

interface ReEntryListEditorProps {
  reEntries: ReEntry[];

  onChange: (
    reEntries: ReEntry[],
  ) => void;
}

type ReEntryNumberField =
  | 'count'
  | 'price'
  | 'stack';

const DEFAULT_REENTRY_PRICE =
  10000;

const DEFAULT_REENTRY_STACK =
  30000;

const ReEntryListEditor:
  React.FC<ReEntryListEditorProps> = ({
    reEntries,
    onChange,
  }) => {
  /**
   * 리엔트리 추가
   */
  const handleAddReEntry =
    (): void => {
      const newCount =
        reEntries.length > 0
          ? Math.max(
              ...reEntries.map(
                (
                  reEntry:
                    ReEntry,
                ) =>
                  reEntry.count,
              ),
            ) + 1
          : 1;

      const newReEntry:
        ReEntry = {
        count:
          newCount,

        price:
          DEFAULT_REENTRY_PRICE,

        stack:
          DEFAULT_REENTRY_STACK,
      };

      onChange(
        [
          ...reEntries,
          newReEntry,
        ].sort(
          (
            first:
              ReEntry,
            second:
              ReEntry,
          ) =>
            first.count -
            second.count,
        ),
      );
    };

  /**
   * 리엔트리 삭제
   */
  const handleRemoveReEntry = (
    count: number,
  ): void => {
    const updated =
      reEntries
        .filter(
          (
            reEntry:
              ReEntry,
          ) =>
            reEntry.count !==
            count,
        )
        .map(
          (
            reEntry:
              ReEntry,
            index:
              number,
          ) => ({
            ...reEntry,

            count:
              index + 1,
          }),
        );

    onChange(
      updated,
    );
  };

  /**
   * 리엔트리 수정
   */
  const handleUpdateReEntry = (
    currentCount:
      number,
    field:
      ReEntryNumberField,
    value:
      number,
  ): void => {
    const updated =
      reEntries
        .map(
          (
            reEntry:
              ReEntry,
          ) =>
            reEntry.count ===
            currentCount
              ? {
                  ...reEntry,

                  [field]:
                    value,
                }
              : reEntry,
        )
        .sort(
          (
            first:
              ReEntry,
            second:
              ReEntry,
          ) =>
            first.count -
            second.count,
        );

    onChange(
      updated,
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-18-medium text-gray-900">
          리엔트리 옵션 (
          {reEntries.length}
          개)
        </h3>

        <button
          type="button"
          onClick={
            handleAddReEntry
          }
          className="flex items-center gap-2 rounded-lg bg-main px-4 py-2 text-16-medium text-white transition-colors hover:bg-main-medium"
        >
          <Plus
            size={16}
          />

          옵션 추가
        </button>
      </div>

      {reEntries.length ===
      0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 py-8 text-center">
          <p className="text-16-regular text-gray-600">
            리엔트리 옵션을
            추가해주세요.
          </p>

          <p className="mt-1 text-14-regular text-gray-500">
            선택사항입니다.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {reEntries.map(
            (
              reEntry:
                ReEntry,
            ) => (
              <div
                key={
                  reEntry.count
                }
                className="rounded-lg border border-gray-300 bg-white p-4"
              >
                {/* Header */}
                <div className="mb-3 flex items-center justify-between">
                  <h4 className="text-16-medium text-gray-900">
                    {
                      reEntry.count
                    }
                    회 리엔트리
                  </h4>

                  <button
                    type="button"
                    onClick={() =>
                      handleRemoveReEntry(
                        reEntry.count,
                      )
                    }
                    className="grid h-8 w-8 place-items-center rounded-lg text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
                    aria-label={`${reEntry.count}회 리엔트리 삭제`}
                  >
                    <X
                      size={20}
                    />
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  {/* Count */}
                  <div>
                    <label className="mb-1 block text-14-regular text-gray-600">
                      횟수
                    </label>

                    <input
                      type="number"
                      value={
                        reEntry.count
                      }
                      onChange={(
                        event,
                      ) =>
                        handleUpdateReEntry(
                          reEntry.count,
                          'count',
                          Number(
                            event
                              .target
                              .value,
                          ),
                        )
                      }
                      min={1}
                      className="w-full rounded border border-gray-300 px-3 py-2 text-16-regular outline-none focus:border-main"
                    />
                  </div>

                  {/* Price */}
                  <div>
                    <label className="mb-1 block text-14-regular text-gray-600">
                      가격 (₩)
                    </label>

                    <input
                      type="number"
                      value={
                        reEntry.price
                      }
                      onChange={(
                        event,
                      ) =>
                        handleUpdateReEntry(
                          reEntry.count,
                          'price',
                          Number(
                            event
                              .target
                              .value,
                          ),
                        )
                      }
                      min={0}
                      step={1000}
                      className="w-full rounded border border-gray-300 px-3 py-2 text-16-regular outline-none focus:border-main"
                    />

                    <p className="mt-1 text-12-regular text-gray-500">
                      {formatCurrency(
                        reEntry.price,
                      )}
                    </p>
                  </div>

                  {/* Stack */}
                  <div>
                    <label className="mb-1 block text-14-regular text-gray-600">
                      스택 (칩)
                    </label>

                    <input
                      type="number"
                      value={
                        reEntry.stack
                      }
                      onChange={(
                        event,
                      ) =>
                        handleUpdateReEntry(
                          reEntry.count,
                          'stack',
                          Number(
                            event
                              .target
                              .value,
                          ),
                        )
                      }
                      min={0}
                      step={1000}
                      className="w-full rounded border border-gray-300 px-3 py-2 text-16-regular outline-none focus:border-main"
                    />

                    <p className="mt-1 text-12-regular text-gray-500">
                      {formatNumber(
                        reEntry.stack,
                      )}{' '}
                      칩
                    </p>
                  </div>
                </div>
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
};

export default ReEntryListEditor;